import React, { useState, useEffect, useCallback } from 'react';
import { FaRobot, FaComments, FaUserFriends, FaChartLine, FaSync, FaClock, FaDesktop, FaMobileAlt, FaExternalLinkAlt, FaEnvelope } from 'react-icons/fa';

const API_URL = window.location.hostname === 'localhost' ? 'http://localhost:5001' : '';
const GA_PROPERTY_ID = 'G-JZV427XLZB';

const cardStyle = {
    background: '#2d2d3f', padding: '20px', borderRadius: '12px',
    border: '1px solid rgba(255,215,0,0.1)', color: '#fff'
};
const statNumberStyle = { fontSize: '2rem', fontWeight: 'bold', color: '#ffd700', marginBottom: '4px' };
const statLabelStyle = { fontSize: '0.85rem', color: '#aaa' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' };
const thStyle = { padding: '10px 8px', textAlign: 'left', borderBottom: '2px solid #444', color: '#ffd700', fontWeight: 600 };
const tdStyle = { padding: '8px', borderBottom: '1px solid #333', color: '#ddd' };

const AnalyticsTab = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [conversationDetail, setConversationDetail] = useState(null);
    const [detailMessages, setDetailMessages] = useState([]);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const fetchStats = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/chat/stats`);
            const data = await res.json();
            if (data.success) setStats(data.stats);
        } catch (err) {
            console.error('Error fetching chat stats:', err);
        }
        setLoading(false);
    }, []);

    useEffect(() => { fetchStats(); }, [fetchStats]);

    const viewConversation = async (sessionId) => {
        setLoadingDetail(true);
        try {
            const res = await fetch(`${API_URL}/api/chat/history?session_id=${sessionId}`);
            const data = await res.json();
            if (data.success) {
                setDetailMessages(data.messages || []);
                setConversationDetail(sessionId);
            }
        } catch (err) {
            console.error('Error loading conversation:', err);
        }
        setLoadingDetail(false);
    };

    const maxHourly = stats?.hourly ? Math.max(...stats.hourly.map(h => h.count), 1) : 1;
    const maxDaily = stats?.daily ? Math.max(...stats.daily.map(d => d.count), 1) : 1;

    return (
        <div className="fade-in">
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaChartLine /> Analytics
                </h3>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button onClick={fetchStats} disabled={loading} style={{
                        background: 'rgba(255,215,0,0.15)', border: '1px solid rgba(255,215,0,0.4)',
                        color: '#ffd700', padding: '8px 15px', borderRadius: '5px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '6px'
                    }}>
                        <FaSync className={loading ? 'spin' : ''} /> Actualizar
                    </button>
                    <a href={`https://analytics.google.com/analytics/web/#/p${GA_PROPERTY_ID}`}
                        target="_blank" rel="noopener noreferrer" style={{
                            background: 'rgba(66,133,244,0.15)', border: '1px solid rgba(66,133,244,0.4)',
                            color: '#4285f4', padding: '8px 15px', borderRadius: '5px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: '6px', textDecoration: 'none'
                        }}>
                        <FaExternalLinkAlt /> Google Analytics
                    </a>
                </div>
            </div>

            {/* Conversation Detail Modal */}
            {conversationDetail && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }} onClick={() => setConversationDetail(null)}>
                    <div style={{
                        background: '#2d2d3f', padding: '24px', borderRadius: '12px',
                        width: '90%', maxWidth: '600px', maxHeight: '80vh', overflowY: 'auto',
                        border: '1px solid #ffd700'
                    }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h4 style={{ margin: 0, color: '#ffd700' }}>Conversacion</h4>
                            <button onClick={() => setConversationDetail(null)} style={{
                                background: 'none', border: 'none', color: '#aaa', fontSize: '1.2rem', cursor: 'pointer'
                            }}>✕</button>
                        </div>
                        {loadingDetail ? <p style={{ color: '#aaa' }}>Cargando...</p> : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {detailMessages.map((msg, i) => (
                                    <div key={i} style={{
                                        padding: '10px 14px', borderRadius: '10px', fontSize: '0.85rem', lineHeight: 1.5,
                                        background: msg.role === 'user' ? 'rgba(255,215,0,0.1)' : 'rgba(59,130,246,0.1)',
                                        borderLeft: `3px solid ${msg.role === 'user' ? '#ffd700' : '#3b82f6'}`,
                                        color: '#ddd'
                                    }}>
                                        <strong style={{ color: msg.role === 'user' ? '#ffd700' : '#3b82f6', fontSize: '0.75rem' }}>
                                            {msg.role === 'user' ? 'Usuario' : 'Asistente'}
                                        </strong>
                                        <div style={{ marginTop: '4px' }}>{msg.content}</div>
                                    </div>
                                ))}
                                {detailMessages.length === 0 && <p style={{ color: '#aaa' }}>Sin mensajes.</p>}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {!stats ? (
                <p style={{ color: '#aaa' }}>{loading ? 'Cargando analytics...' : 'No hay datos disponibles.'}</p>
            ) : (
                <>
                    {/* KPI Cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px', marginBottom: '25px' }}>
                        <div style={cardStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                <FaComments color="#ffd700" /> <span style={{ fontSize: '0.8rem', color: '#aaa' }}>Total Conversaciones</span>
                            </div>
                            <div style={statNumberStyle}>{stats.total_conversations}</div>
                            <div style={statLabelStyle}>{stats.total_messages} mensajes totales</div>
                        </div>
                        <div style={cardStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                <FaClock color="#4caf50" /> <span style={{ fontSize: '0.8rem', color: '#aaa' }}>Hoy</span>
                            </div>
                            <div style={{ ...statNumberStyle, color: '#4caf50' }}>{stats.today}</div>
                            <div style={statLabelStyle}>conversaciones hoy</div>
                        </div>
                        <div style={cardStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                <FaChartLine color="#2196f3" /> <span style={{ fontSize: '0.8rem', color: '#aaa' }}>Ultima Semana</span>
                            </div>
                            <div style={{ ...statNumberStyle, color: '#2196f3' }}>{stats.this_week}</div>
                            <div style={statLabelStyle}>conversaciones 7 dias</div>
                        </div>
                        <div style={cardStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                <FaRobot color="#ff9800" /> <span style={{ fontSize: '0.8rem', color: '#aaa' }}>Por Origen</span>
                            </div>
                            <div style={{ fontSize: '0.9rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                    <span><FaDesktop size={12} /> Dashboard</span>
                                    <span style={{ color: '#ffd700', fontWeight: 'bold' }}>{stats.by_mode.dashboard || 0}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span><FaMobileAlt size={12} /> Landing</span>
                                    <span style={{ color: '#ffd700', fontWeight: 'bold' }}>{stats.by_mode.landing || 0}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Charts Row */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '25px' }}>
                        {/* Daily Chart */}
                        <div style={cardStyle}>
                            <h4 style={{ margin: '0 0 15px', fontSize: '0.95rem' }}>Conversaciones por Dia (14 dias)</h4>
                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '120px' }}>
                                {stats.daily.map((d, i) => (
                                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <span style={{ fontSize: '0.6rem', color: '#aaa', marginBottom: '4px' }}>{d.count}</span>
                                        <div style={{
                                            width: '100%', background: 'linear-gradient(180deg, #ffd700, #ff9800)',
                                            borderRadius: '3px 3px 0 0',
                                            height: `${(d.count / maxDaily) * 100}px`,
                                            minHeight: '2px', transition: 'height 0.3s'
                                        }} title={`${d.day}: ${d.count}`} />
                                        <span style={{ fontSize: '0.55rem', color: '#666', marginTop: '4px', transform: 'rotate(-45deg)', whiteSpace: 'nowrap' }}>
                                            {d.day.substring(5)}
                                        </span>
                                    </div>
                                ))}
                                {stats.daily.length === 0 && <p style={{ color: '#666', fontSize: '0.8rem' }}>Sin datos aun</p>}
                            </div>
                        </div>

                        {/* Hourly Chart */}
                        <div style={cardStyle}>
                            <h4 style={{ margin: '0 0 15px', fontSize: '0.95rem' }}>Horarios Pico</h4>
                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '120px' }}>
                                {stats.hourly.map((h, i) => (
                                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <div style={{
                                            width: '100%', background: `rgba(66,133,244,${0.3 + (h.count / maxHourly) * 0.7})`,
                                            borderRadius: '2px 2px 0 0',
                                            height: `${(h.count / maxHourly) * 100}px`,
                                            minHeight: '2px', transition: 'height 0.3s'
                                        }} title={`${h.hour}:00 - ${h.count} conv.`} />
                                        <span style={{ fontSize: '0.55rem', color: '#666', marginTop: '2px' }}>
                                            {h.hour}
                                        </span>
                                    </div>
                                ))}
                                {stats.hourly.length === 0 && <p style={{ color: '#666', fontSize: '0.8rem' }}>Sin datos aun</p>}
                            </div>
                        </div>
                    </div>

                    {/* Two columns: Top Users + Recent Conversations */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        {/* Top Users */}
                        <div style={cardStyle}>
                            <h4 style={{ margin: '0 0 15px', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <FaUserFriends color="#ffd700" /> Top Usuarios del Chat
                            </h4>
                            {stats.top_users.length === 0 ? (
                                <p style={{ color: '#666', fontSize: '0.85rem' }}>Aun no hay usuarios registrados.</p>
                            ) : (
                                <table style={tableStyle}>
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>Usuario</th>
                                            <th style={{ ...thStyle, textAlign: 'center' }}>Conv.</th>
                                            <th style={{ ...thStyle, textAlign: 'center' }}>Msgs</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {stats.top_users.map((u, i) => (
                                            <tr key={i}>
                                                <td style={tdStyle}>
                                                    <div style={{ fontWeight: 500 }}>{u.user_name || 'Sin nombre'}</div>
                                                    <div style={{ fontSize: '0.7rem', color: '#888' }}>
                                                        <FaEnvelope size={9} /> {u.user_email || u.user_id?.substring(0, 12) + '...'}
                                                    </div>
                                                </td>
                                                <td style={{ ...tdStyle, textAlign: 'center' }}>{u.conversations}</td>
                                                <td style={{ ...tdStyle, textAlign: 'center', color: '#ffd700', fontWeight: 'bold' }}>{u.total_msgs}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Recent Conversations */}
                        <div style={cardStyle}>
                            <h4 style={{ margin: '0 0 15px', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <FaComments color="#4caf50" /> Conversaciones Recientes
                            </h4>
                            <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                <table style={tableStyle}>
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>Usuario</th>
                                            <th style={{ ...thStyle, textAlign: 'center' }}>Origen</th>
                                            <th style={{ ...thStyle, textAlign: 'center' }}>Msgs</th>
                                            <th style={thStyle}>Fecha</th>
                                            <th style={thStyle}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {stats.recent.map((c, i) => (
                                            <tr key={i}>
                                                <td style={tdStyle}>
                                                    {c.user_name || c.user_email || <span style={{ color: '#666' }}>Visitante</span>}
                                                </td>
                                                <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                    <span style={{
                                                        padding: '2px 8px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 600,
                                                        background: c.mode === 'dashboard' ? 'rgba(255,215,0,0.15)' : 'rgba(76,175,80,0.15)',
                                                        color: c.mode === 'dashboard' ? '#ffd700' : '#4caf50'
                                                    }}>
                                                        {c.mode === 'dashboard' ? 'Dash' : 'Landing'}
                                                    </span>
                                                </td>
                                                <td style={{ ...tdStyle, textAlign: 'center' }}>{c.message_count}</td>
                                                <td style={{ ...tdStyle, fontSize: '0.75rem', color: '#888' }}>
                                                    {new Date(c.updated_at).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                                </td>
                                                <td style={tdStyle}>
                                                    <button onClick={() => viewConversation(c.session_id)} style={{
                                                        background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)',
                                                        color: '#3b82f6', padding: '4px 10px', borderRadius: '4px',
                                                        cursor: 'pointer', fontSize: '0.7rem'
                                                    }}>Ver</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Google Analytics Section */}
                    <div style={{ ...cardStyle, marginTop: '20px' }}>
                        <h4 style={{ margin: '0 0 15px', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FaChartLine color="#4285f4" /> Google Analytics
                        </h4>
                        <p style={{ color: '#aaa', fontSize: '0.85rem', marginBottom: '15px' }}>
                            Google Analytics esta activo en xcoin.cl con el ID <code style={{ color: '#ffd700' }}>{GA_PROPERTY_ID}</code>.
                            Los datos completos de trafico, fuentes, paginas y comportamiento de usuarios estan disponibles en el panel de Google.
                        </p>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <a href="https://analytics.google.com/analytics/web/" target="_blank" rel="noopener noreferrer"
                                style={{
                                    background: 'linear-gradient(135deg, #4285f4, #34a853)', color: '#fff',
                                    padding: '10px 20px', borderRadius: '8px', textDecoration: 'none',
                                    fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px'
                                }}>
                                <FaExternalLinkAlt /> Abrir Google Analytics
                            </a>
                            <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer"
                                style={{
                                    background: 'rgba(255,255,255,0.08)', color: '#ddd',
                                    padding: '10px 20px', borderRadius: '8px', textDecoration: 'none',
                                    border: '1px solid #444', display: 'flex', alignItems: 'center', gap: '8px'
                                }}>
                                <FaExternalLinkAlt /> Search Console
                            </a>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default AnalyticsTab;
